import { FetchError, UserReferral } from "@decibeltrade/sdk";
import { type FetchQueryOptions, useQuery } from "@tanstack/react-query";

import { readDexSdk } from "../dex-sdk";

export const USER_REFERRALS_QUERY_KEY = "userReferrals";

/**
 * The endpoint defaults to 10 rows and caps at 1000 (handle_referrals.rs:223).
 * Anything that counts referrals must request the cap explicitly — the default
 * would silently truncate to the 10 most recent.
 */
const PAGE_LIMIT = 1000;

export function userReferralsQueryKey(referrerAccount: string | undefined) {
  return [USER_REFERRALS_QUERY_KEY, referrerAccount] as const;
}

export function userReferralsQuery(referrerAccount: string) {
  return {
    queryKey: userReferralsQueryKey(referrerAccount),
    queryFn: async () => {
      if (!referrerAccount) return [];
      try {
        return await readDexSdk.referrals.getUserReferrals({
          referrerAccount,
          limit: PAGE_LIMIT,
        });
      } catch (error) {
        // An account that has never referred anyone has no row, not an error.
        if (error instanceof FetchError && error.status === 404) return [];
        throw error;
      }
    },
    retry(failureCount: number, error: Error) {
      if (error instanceof FetchError && error.status === 404) return false;
      return failureCount < 3;
    },
  } satisfies FetchQueryOptions<UserReferral[]>;
}

export function useUserReferralsQuery(referrerAccount: string | undefined) {
  return useQuery({
    ...userReferralsQuery(referrerAccount ?? ""),
    enabled: !!referrerAccount,
  });
}
